import React from 'react';
import { Link } from 'react-router-dom';
import LegalPageLayout from './LegalPageLayout';

// NOTE: This is a working draft. Replace this content with your finalized
// legal text (or paste it into this file directly — the layout will
// remain consistent).

export default function TermsPage() {
  const effectiveDate = 'February 1, 2026';
  return (
    <LegalPageLayout
      title="Terms of Service"
      subtitle={`Effective ${effectiveDate}. Please read these terms carefully before using Golden Tips.`}
    >
      <section>
        <h2 className="text-xl font-heading font-semibold text-white mt-2 mb-2">1. Acceptance of terms</h2>
        <p>
          By creating an account, sharing your Golden Tips link, or sending a
          tip through Golden Tips (&ldquo;the Service&rdquo;), you agree to
          these Terms of Service and our{' '}
          <Link to="/privacy" className="text-[#F5C542] hover:underline">Privacy Policy</Link>.
          If you do not agree, do not use the Service.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-heading font-semibold text-white mt-2 mb-2">2. Eligibility</h2>
        <p>
          You must be at least 18 years old and legally able to enter into a
          binding contract in your jurisdiction to use Golden Tips as a
          Recipient. Guests sending tips must be old enough to hold a valid
          payment method under their local laws.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-heading font-semibold text-white mt-2 mb-2">3. What Golden Tips does</h2>
        <p>
          Golden Tips is a platform that lets service professionals
          (&ldquo;Recipients&rdquo;) receive gratuities from customers
          (&ldquo;Guests&rdquo;) through Stripe Connect. Golden Tips is not a
          bank, is not a party to the underlying service relationship between
          Recipient and Guest, and does not itself take custody of tipped
          funds — Stripe processes payment and routes funds to the
          Recipient&apos;s connected account.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-heading font-semibold text-white mt-2 mb-2">4. Fees</h2>
        <p>
          Golden Tips charges a <strong>3% platform fee</strong> on every
          successful tip. The fee is deducted from the tip amount at the time
          of payment, and the remaining amount is transferred to the
          Recipient&apos;s connected Stripe account. Stripe may charge
          additional payment-processing fees according to its own terms.
        </p>
        <p>
          Recipients who opt into the Top Earner Challenge pay an
          <strong> additional 1% during any month they are enrolled</strong>.
          The extra 1% is contributed to that month&apos;s Challenge pool.
          Full rules are below under{' '}
          <a href="#challenge-rules" className="text-[#F5C542] hover:underline">
            Top Earner Challenge Rules
          </a>.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-heading font-semibold text-white mt-2 mb-2">5. Recipient obligations</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>Provide accurate identity and payout information via Stripe Connect and keep it current.</li>
          <li>Use the Service only for legitimate gratuities related to services you actually provide.</li>
          <li>Comply with all applicable tax laws — Golden Tips does not withhold tax or file returns on your behalf.</li>
          <li>Not misrepresent your identity, business, or the nature of a payment.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-heading font-semibold text-white mt-2 mb-2">6. Guest tippers</h2>
        <p>
          Tips are voluntary and non-refundable in most cases. If you believe
          a tip was made in error, unauthorized, or duplicated, contact us at{' '}
          <Link to="/contact" className="text-[#F5C542] hover:underline">
            /contact
          </Link>{' '}
          within 14 days of the transaction and we will work with the
          Recipient and Stripe in good faith.
        </p>
      </section>

      <section id="challenge-rules">
        <h2 className="text-xl font-heading font-semibold text-white mt-2 mb-2">
          7. Top Earner Challenge — full rules
        </h2>
        <p>
          The Top Earner Challenge is an optional monthly competition open to
          Recipients on Golden Tips. Participation is opt-in and can be
          revoked between periods.
        </p>
        <p>
          <strong>Additional 1% fee.</strong> While enrolled, Recipients pay
          an additional 1% on top of the standard 3% platform fee for every
          tip that settles during that calendar month. The additional 1% is
          contributed to that month&apos;s prize pool. This means participants
          effectively pay 4% during periods they are enrolled.
        </p>
        <p>
          <strong>Qualifying to appear on the leaderboard.</strong>{' '}
          Recipients must receive at least five (5) successful tips during
          the calendar month to qualify. Test tips, refunded tips, and
          disputed tips do not count.
        </p>
        <p>
          <strong>Categories &amp; payout.</strong> Two categories are ranked
          each month:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li><em>Top Earner</em> — by total tips received in the period.</li>
          <li><em>Highest Tip Rate</em> — by average tip size in the period.</li>
        </ul>
        <p>
          If the pool is at or below $150 at close, the entire pool goes to
          the #1 Top Earner (winner-take-all). If the pool exceeds $150, 70%
          of the pool is split across the Top Earner podium (50/30/20 for
          1st/2nd/3rd) and 30% is split across the Highest Tip Rate podium on
          the same ratio.
        </p>
        <p>
          <strong>Ineligible winners.</strong> A winner must have Stripe
          payouts enabled at the moment payout runs. If a winner is
          ineligible, their share shifts to the next eligible Recipient in
          line within the same category.
        </p>
        <p>
          <strong>Close &amp; payout schedule.</strong> Periods close at or
          shortly after the first day of the following calendar month. Golden
          Tips runs payouts via Stripe Transfer to each winner&apos;s
          connected account. Winners will see funds according to their
          Stripe payout schedule.
        </p>
        <p>
          <strong>Sportsmanship.</strong> Fabricated tips, self-tipping, and
          any behavior intended to game the ranking result in disqualification
          and possible account suspension. Golden Tips reserves the right to
          exclude any Recipient from the Challenge at its discretion.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-heading font-semibold text-white mt-2 mb-2">8. Prohibited uses</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>Money laundering, fraud, or any illegal activity.</li>
          <li>Payments for goods or services regulated as such by law (e.g., alcohol delivery, controlled substances).</li>
          <li>Chargebacks initiated in bad faith.</li>
          <li>Impersonation of another Recipient or business.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-heading font-semibold text-white mt-2 mb-2">9. Limitation of liability</h2>
        <p>
          Golden Tips is provided &ldquo;as is&rdquo; without warranties of
          any kind. To the maximum extent permitted by law, Golden Tips shall
          not be liable for indirect, incidental, or consequential damages
          arising from your use of the Service.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-heading font-semibold text-white mt-2 mb-2">10. Changes to these terms</h2>
        <p>
          We may update these Terms from time to time. Material changes will
          be communicated via email or an in-app notice at least 14 days
          before they take effect.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-heading font-semibold text-white mt-2 mb-2">11. Contact</h2>
        <p>
          Questions about these Terms?{' '}
          <Link to="/contact" className="text-[#F5C542] hover:underline">Contact us</Link>.
        </p>
      </section>
    </LegalPageLayout>
  );
}
